import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/organization-dashboard',
      icon: 'LayoutDashboard',
      badge: null
    },
    {
      label: 'Vaults',
      path: '/vault-management',
      icon: 'Vault',
      badge: null
    },
    {
      label: 'Sharing',
      path: '/sharing-management',
      icon: 'Share2',
      badge: 3
    },
    {
      label: 'Users',
      path: '/user-management',
      icon: 'Users',
      badge: null
    },
    {
      label: 'Account',
      path: '/account-settings',
      icon: 'Settings',
      badge: null
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo Section */}
      <div className="flex items-center p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Shield" size={20} color="white" />
          </div>
          {!isCollapsed && (
            <div className="min-w-0">
              <span className="text-xl font-semibold text-foreground block truncate">
                SecureVault
              </span>
              <span className="text-xs text-muted-foreground block truncate">
                Enterprise
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {navigationItems?.map((item) => {
          const isActive = isActivePath(item?.path);
          
          return (
            <Button
              key={item?.path}
              variant={isActive ? "default" : "ghost"}
              onClick={() => handleNavigation(item?.path)}
              className={`w-full justify-start h-12 ${
                isCollapsed ? 'px-3' : 'px-4'
              } ${isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`}
            >
              <Icon 
                name={item?.icon} 
                size={20} 
                className={isCollapsed ? '' : 'mr-3'} 
              />
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left">{item?.label}</span>
                  {item?.badge && (
                    <span className="bg-accent text-accent-foreground text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                      {item?.badge}
                    </span>
                  )}
                </>
              )}
            </Button>
          );
        })}
      </nav>

      {/* Collapse Toggle (Desktop Only) */}
      {onToggleCollapse && (
        <div className="p-4 border-t border-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="w-full h-10"
          >
            <Icon 
              name={isCollapsed ? "ChevronRight" : "ChevronLeft"} 
              size={20} 
            />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:z-100 lg:flex-col bg-card border-r border-border transition-all duration-300 ${
        isCollapsed ? 'lg:w-16' : 'lg:w-72'
      }`}>
        <SidebarContent />
      </aside>
      {/* Mobile Sidebar Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-300"
      >
        <Icon name="Menu" size={20} />
      </Button>
      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-300">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsMobileOpen(false)}
          />
          
          {/* Sidebar */}
          <aside className="fixed inset-y-0 left-0 w-72 bg-card border-r border-border flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={20} color="white" />
                </div>
                <div>
                  <span className="text-xl font-semibold text-foreground block">
                    SecureVault
                  </span>
                  <span className="text-xs text-muted-foreground block">
                    Enterprise
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileOpen(false)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <nav className="flex-1 p-4 space-y-2">
              {navigationItems?.map((item) => {
                const isActive = isActivePath(item?.path);
                
                return (
                  <Button
                    key={item?.path}
                    variant={isActive ? "default" : "ghost"}
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full justify-start h-12 px-4 ${
                      isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} className="mr-3" />
                    <span className="flex-1 text-left">{item?.label}</span>
                    {item?.badge && (
                      <span className="bg-accent text-accent-foreground text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                        {item?.badge}
                      </span>
                    )}
                  </Button>
                );
              })}
            </nav>
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;